import type { PremiumTier } from "./dataLayer/backendApi";

export type Store = "apple" | "google";
export interface StoreProduct {
  productId: string;
  tier: PremiumTier;
  basePlanId?: string;
}
export interface StorePlan extends StoreProduct {
  title: string;
  price: string;
  period: string;
}
export interface StoreTransaction {
  transactionId?: string;
  purchaseToken?: string;
  appleEnvironment?: "Production" | "Sandbox";
}
export interface NativeBillingPlugin {
  getBillingContext?(): Promise<{ signedAppTransaction?: string }>;
  manageSubscriptions?(): Promise<void>;
  addListener?(
    event: "purchasesUpdated",
    listener: () => void,
  ): { remove(): Promise<void> } | Promise<{ remove(): Promise<void> }>;
  getProducts(options: {
    products: StoreProduct[];
  }): Promise<{ products: Omit<StorePlan, "tier">[] }>;
  purchase(options: { productId: string; basePlanId?: string; accountToken: string }): Promise<{
    state: "purchased" | "pending" | "cancelled";
    transactions: StoreTransaction[];
  }>;
  restorePurchases(options: { sync: boolean }): Promise<{ transactions: StoreTransaction[] }>;
  finish(options: { transactionId: string }): Promise<void>;
}

export async function storeBillingContext(): Promise<{ signedAppTransaction?: string }> {
  const plugin = window.Capacitor?.Plugins?.WirderBilling;
  if (nativeStore() !== "apple" || !plugin?.getBillingContext) return {};
  return plugin.getBillingContext();
}

export function nativeStore(): Store | null {
  const platform = window.Capacitor?.getPlatform?.();
  return platform === "ios" ? "apple" : platform === "android" ? "google" : null;
}

export function billingPlugin(): NativeBillingPlugin {
  const plugin = window.Capacitor?.Plugins?.WirderBilling;
  if (!nativeStore() || !plugin)
    throw new Error("Please update the Wirder app to use store subscriptions.");
  return plugin;
}

export function matchStorePlans(
  configured: StoreProduct[],
  available: Omit<StorePlan, "tier">[],
): StorePlan[] {
  return configured.flatMap((product) => {
    const offer = available.find(
      (item) => item.productId === product.productId && item.basePlanId === product.basePlanId,
    );
    if (!offer || !offer.price || !/^P(?:\d+[YMWD])+$/.test(offer.period)) return [];
    return [{ ...product, title: offer.title, price: offer.price, period: offer.period }];
  });
}

export function periodLabel(period: string): string {
  const match = /^P(\d+)([YMWD])$/.exec(period);
  if (!match) return period;
  const unit = { Y: "year", M: "month", W: "week", D: "day" }[match[2]];
  return match[1] === "1" ? `per ${unit}` : `every ${match[1]} ${unit}s`;
}

export async function registerStorePurchaseUpdates(onUpdate: () => Promise<void>): Promise<void> {
  const plugin = window.Capacitor?.Plugins?.WirderBilling;
  if (!plugin?.addListener) return;
  try {
    await plugin.addListener("purchasesUpdated", () => {
      void onUpdate().catch(() => console.error("Store purchase update failed"));
    });
  } catch {
    console.error("Could not register store purchase updates");
  }
}
